import { InMemoryCache, useApolloClient } from "@apollo/client";
import { Box, GlobalStyles, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { useCopyToClipboard } from "hooks/useCopyToClipboard";
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import { useRegisterCommands, type Command } from "../CommandPaletteRegistry";
import { buildIdTypeIndex, resolvePickTarget, type PickTarget } from "./idTypeIndex";

type PickerAction = "lookup" | "id";

type HoveredTarget = {
  id: string;
  type: string | null;
  rect: DOMRect;
};

function labelFor(target: { id: string; type: string | null }): string {
  return `${target.type ?? "Record"} ${target.id}`;
}

const CHIP_GAP = 8;
const CHIP_HEIGHT = 36;
const CHIP_MAX_WIDTH = 420;

function actionForEvent(event: MouseEvent): PickerAction {
  // Looking the record up is the common case — the lookup dialog already
  // offers its permalink, JSON, and audit history from there.
  return event.altKey ? "id" : "lookup";
}

function HighlightBox({ target }: { target: HoveredTarget }) {
  const { rect } = target;
  // Flip the chip above the highlight when the target sits near the bottom
  // edge, then clamp both axes: a tall row scrolled partway off the top, or a
  // narrow viewport, would otherwise place it at a negative offset.
  const chipBelow = rect.bottom + CHIP_GAP + CHIP_HEIGHT < window.innerHeight;
  const chipTop = Math.max(CHIP_GAP, chipBelow ? rect.bottom + CHIP_GAP : rect.top - CHIP_HEIGHT);
  const chipLeft = Math.max(CHIP_GAP, Math.min(rect.left, window.innerWidth - CHIP_MAX_WIDTH));

  return (
    <>
      <Box
        sx={(theme) => ({
          position: "fixed",
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
          border: 2,
          borderColor: theme.palette.primary.main,
          borderRadius: 1,
          backgroundColor: theme.palette.primary.main,
          opacity: 0.16,
        })}
      />
      <Box
        sx={(theme) => ({
          position: "fixed",
          top: chipTop,
          left: chipLeft,
          maxWidth: CHIP_MAX_WIDTH,
          px: 1.25,
          py: 0.5,
          borderRadius: 1,
          display: "flex",
          alignItems: "baseline",
          gap: 1,
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.primary.contrastText,
          boxShadow: theme.shadows[3],
        })}
      >
        <Typography variant="caption" fontWeight={700} noWrap>
          {target.type ?? "Unknown type"}
        </Typography>
        <Typography variant="caption" sx={{ fontFamily: "monospace", opacity: 0.9 }} noWrap>
          {target.id}
        </Typography>
      </Box>
    </>
  );
}

function PickerLegend({ hasTarget }: { hasTarget: boolean }) {
  const hints = ["click — look up record", "⌥ click — copy raw ID", "esc — cancel"];

  return (
    <Box
      sx={(theme) => ({
        position: "fixed",
        bottom: 24,
        left: "50%",
        transform: "translateX(-50%)",
        px: 2,
        py: 1,
        borderRadius: 2,
        display: "flex",
        gap: 2,
        flexWrap: "wrap",
        justifyContent: "center",
        backgroundColor: theme.palette.grey[900],
        color: theme.palette.common.white,
        boxShadow: theme.shadows[6],
      })}
    >
      <Typography variant="caption" fontWeight={700}>
        {hasTarget ? "Pick an ID" : "Hover a grid row or record block"}
      </Typography>
      {hints.map((hint) => (
        <Typography key={hint} variant="caption" sx={{ opacity: 0.75 }}>
          {hint}
        </Typography>
      ))}
    </Box>
  );
}

/**
 * Owns picker-mode eventing: tracks what's under the cursor and swallows the
 * interaction so picking a row can't also trigger that row's own navigation.
 * Returns the currently hovered target for the overlay to draw.
 */
function usePickerEvents(
  active: boolean,
  onPick: (target: PickTarget, action: PickerAction) => void,
  onCancel: () => void
): HoveredTarget | null {
  const client = useApolloClient();
  const [hovered, setHovered] = useState<HoveredTarget | null>(null);
  const typeIndexRef = useRef<ReadonlyMap<string, string>>(new Map());
  const elementRef = useRef<HTMLElement | null>(null);
  const pointerRef = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    if (!active) return;

    // One snapshot per activation: extract() walks the whole normalized store,
    // which is far too heavy to redo on every mousemove. Only InMemoryCache
    // serializes to the normalized `Type:id` shape the index reads.
    const { cache } = client;
    typeIndexRef.current =
      cache instanceof InMemoryCache ? buildIdTypeIndex(cache.extract()) : new Map();

    const applyTarget = (next: PickTarget | null) => {
      elementRef.current = next?.element ?? null;
      setHovered(
        next === null
          ? null
          : { id: next.id, type: next.type, rect: next.element.getBoundingClientRect() }
      );
    };

    const onMouseMove = (event: MouseEvent) => {
      pointerRef.current = { x: event.clientX, y: event.clientY };
      const next = resolvePickTarget(event.target, typeIndexRef.current);
      if (next?.element === elementRef.current) return;
      applyTarget(next);
    };

    // Scrolling moves rows under a stationary cursor, so re-resolve from the
    // last pointer position rather than just repositioning the old highlight.
    // Coalesced onto a frame: `scroll` is capture-phase (so every inner
    // scroller feeds it) and both elementFromPoint and getBoundingClientRect
    // force layout, which is enough to make momentum scrolling stutter.
    let frame: number | null = null;
    const onViewportChange = () => {
      if (frame !== null) return;
      frame = window.requestAnimationFrame(() => {
        frame = null;
        const pointer = pointerRef.current;
        if (pointer === null) return;
        applyTarget(
          resolvePickTarget(document.elementFromPoint(pointer.x, pointer.y), typeIndexRef.current)
        );
      });
    };

    // Capture-phase so the underlying row never sees the interaction.
    const swallow = (event: MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
    };

    const onClick = (event: MouseEvent) => {
      swallow(event);
      // Resolve from the click itself: a click with no intervening mousemove
      // (activating from the keyboard with the cursor already over a row) never
      // populated a hover target, and the event target is fresher regardless.
      const target = resolvePickTarget(event.target, typeIndexRef.current);
      if (target === null) {
        onCancel();
        return;
      }
      onPick(target, actionForEvent(event));
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        onCancel();
        return;
      }
      // Let the palette's own ⌘K through, but leave picker mode first —
      // otherwise the capture-phase click swallow makes that palette unusable.
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        onCancel();
      }
    };

    window.addEventListener("mousemove", onMouseMove, { passive: true });
    window.addEventListener("scroll", onViewportChange, { passive: true, capture: true });
    window.addEventListener("resize", onViewportChange, { passive: true });
    window.addEventListener("mousedown", swallow, true);
    window.addEventListener("mouseup", swallow, true);
    window.addEventListener("contextmenu", swallow, true);
    window.addEventListener("click", onClick, true);
    window.addEventListener("keydown", onKeyDown, true);

    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("scroll", onViewportChange, true);
      window.removeEventListener("resize", onViewportChange);
      window.removeEventListener("mousedown", swallow, true);
      window.removeEventListener("mouseup", swallow, true);
      window.removeEventListener("contextmenu", swallow, true);
      window.removeEventListener("click", onClick, true);
      window.removeEventListener("keydown", onKeyDown, true);
      if (frame !== null) window.cancelAnimationFrame(frame);
      elementRef.current = null;
      pointerRef.current = null;
      // Release the cache snapshot: it holds an entry per normalized entity and
      // would otherwise stay retained for the rest of the session.
      typeIndexRef.current = new Map();
      setHovered(null);
    };
  }, [active, client, onPick, onCancel]);

  return hovered;
}

export type CopyIdPickerHandle = {
  start: () => void;
};

/**
 * Picker mode: hover any grid row or `data-copy-id` block to see the record's
 * id and owning model, then click to look that record up.
 */
export const CopyIdPicker = React.forwardRef<
  CopyIdPickerHandle,
  { onLookupId: (id: string) => void }
>(function CopyIdPicker({ onLookupId }, ref): React.ReactElement | null {
  const alerts = useAlerts();
  const copyToClipboard = useCopyToClipboard();
  const [active, setActive] = useState(false);

  const start = useCallback(() => setActive(true), []);
  const stop = useCallback(() => setActive(false), []);

  useImperativeHandle(ref, () => ({ start }), [start]);

  const handlePick = useCallback(
    (target: PickTarget, action: PickerAction) => {
      setActive(false);
      if (action === "lookup") {
        onLookupId(target.id);
        return;
      }
      void Promise.resolve(copyToClipboard(target.id)).then(
        () => alerts.success(`Copied ${labelFor(target)} ID`),
        (error) => alerts.error("Could not copy the ID to the clipboard", error)
      );
    },
    [alerts, copyToClipboard, onLookupId]
  );

  const hovered = usePickerEvents(active, handlePick, stop);

  const commands = useMemo<Command[]>(
    () => [
      {
        id: "pick-id-from-page",
        label: "Pick ID from page",
        description: "Click any grid row or record to look it up",
        type: "goto",
        keywords: ["pick", "copy", "id", "uuid", "record", "picker", "lookup", "inspect"],
        signature: "pick-id-from-page",
        priority: -10,
        run: start,
      },
    ],
    [start]
  );

  useRegisterCommands(commands, [commands], "pick-id-from-page");

  if (!active) return null;

  return createPortal(
    <>
      <GlobalStyles styles={{ "*": { cursor: "crosshair !important" } }} />
      <Box
        sx={(theme) => ({
          position: "fixed",
          inset: 0,
          zIndex: theme.zIndex.tooltip + 1,
          pointerEvents: "none",
        })}
      >
        {hovered !== null && <HighlightBox target={hovered} />}
        <PickerLegend hasTarget={hovered !== null} />
      </Box>
    </>,
    document.body
  );
});
